import React, { useEffect, useMemo } from 'react';
import { shuffle } from 'lodash';
import { checkValue, dealHelper, appendMcicid } from 'src/components/common/helper';
import useCustomerPropertySearchByIdDynamic from '../../../expedia-deals/hotel/hooks/useCustomerPropertySearchByIdDynamic';
import { PropertyDateRangeInput } from 'src/common/__generated__/api/types';
import { useBexApi } from '@shared-ui/bex-api-context';
import DealCardView from 'src/components/common/modules/v1/deal-fixtures/components/deal-card-view';
const DealCardByIds: React.FC = (props: any) => {
  const { context } = useBexApi();
  const initialVariables = {
    // Set your initial variables here
    dateRange: '',
    hotelIds: [],
    // other variables...
  };

  const { hotelSearchData, updateVariables, fetchHotelData, isLoading } =
    useCustomerPropertySearchByIdDynamic(initialVariables);
  const {
    resetSearchResults,
    hotelIds,
    checkInDate,
    checkOutDate,
    regionName = '',
    mccidData,
    randomizeHotelids,
    hotelIdsSecondRow,
  } = props;

  const hotelIdsArray = useMemo(() => {
    const hotelIdsFirstArray = checkValue(hotelIds) ? hotelIds?.split(',') : [];
    const hotelIdsSecondArray = checkValue(hotelIdsSecondRow) ? hotelIdsSecondRow?.split(',') : [];
    return [hotelIdsFirstArray, hotelIdsSecondArray];
  }, [hotelIds, hotelIdsSecondRow]);

  const filterResults = (resultsList: any, ids: string[]) => {
    return resultsList.filter((item: any) => ids.includes(item.id));
  };

  const getAvailableResults = (arr: any) => {
    if (!checkValue(arr, true)) return [];
    return arr.filter((f: any) => {
        let priceValue =
          checkValue(f.price) && checkValue(f.price?.lead) && checkValue(f.price?.lead?.formatted)
            ? f.price?.lead?.formatted
            : undefined;
        // Check If return 0 price update to not visible
        if (checkValue(priceValue) && priceValue.indexOf('0') > -1) {
          const onlyPrice = priceValue.replace(/\D/g, '').trim();
          if (Number(onlyPrice) < 1) {
            priceValue = undefined;
          }
        }        
        if (checkValue(priceValue)) {
          return f;
        }
    });
  };

  const getDisplayList = (fullList: any, availableList: any) =>
    checkValue(availableList, true) ? availableList : fullList;

  const randomize = (list: any) => {
    if (checkValue(list, true) && list?.length > 1 && checkValue(randomizeHotelids) && randomizeHotelids === 'True') {
      return shuffle(list);
    } else return list;
  };

  useEffect(() => {
    updateVariables(initialVariables);
    fetchHotelData();
  }, [resetSearchResults]);

  const { checkInDateFormattedInputFormat, checkOutDateFormattedInputFormat } = dealHelper.getCheckInCheckOutDates(
    checkInDate,
    checkOutDate
  );

  const getMoreDealsLink = (hotelIds: string[]) => {
    let moreDealsLink = '';
    if ((Array.isArray(hotelIds) && hotelIds.length > 0) && checkValue(regionName)) {
      const checkIn = `${checkInDateFormattedInputFormat.year}-${checkInDateFormattedInputFormat.month}-${checkInDateFormattedInputFormat.day}`;
      const checkOut = `${checkOutDateFormattedInputFormat.year}-${checkOutDateFormattedInputFormat.month}-${checkOutDateFormattedInputFormat.day}`;
      if (hotelIds.length > 2) {
        moreDealsLink = `/go/hotel/search/Destination/${checkIn}/${checkOut}?Group=${hotelIds.join(
          ','
        )}&CityName=${regionName}&SortBy=distance&NumRoom=1&NumAdult1=1`;
      } else {
        moreDealsLink = `/go/hotel/search/Destination/${checkIn}/${checkOut}?CityName=${regionName}&SortBy=distance&NumRoom=1&NumAdult1=1`;
      }
      return (moreDealsLink = appendMcicid(
        moreDealsLink,
        mccidData?.templateType,
        mccidData?.campaignUrl,
        mccidData?.mainUrl,
        mccidData?.referrerId
      ));
    }
  };

  const getHotelDataFetchProps = () => {
    const propertyDateRangeInput: PropertyDateRangeInput = {
      checkInDate: checkInDateFormattedInputFormat,
      checkOutDate: checkOutDateFormattedInputFormat,
    };

    const customerPropertySearchByIdFullQueryQueryVariables = {
      context,
      dateRange: propertyDateRangeInput,
      hotelIds: hotelIdsArray.flat() || [],
    };

    return customerPropertySearchByIdFullQueryQueryVariables;
  };

  const handleClick = () => {
    updateVariables(getHotelDataFetchProps());
    fetchHotelData();
  };

  const { moreDealsLink, deals }: any = useMemo(() => {
    if (checkValue(hotelSearchData)) {
      const results = hotelSearchData?.propertySearch?.propertySearchListings?.filter(
        (el) => el?.__typename === 'Property'
      );
      if (checkValue(results, true)) {
        if (checkValue(hotelIds) && checkValue(hotelIdsSecondRow)) {
            const firstList = checkValue(hotelIdsArray?.[0], true) ? filterResults(results, hotelIdsArray?.[0]) : [];
            const availableFirstList = getAvailableResults(firstList);
            const secondList = checkValue(hotelIdsArray?.[1], true) ? filterResults(results, hotelIdsArray?.[1]) : [];
            const availableSecondList = getAvailableResults(secondList);

            const firstDisplayList = randomize(getDisplayList(firstList, availableFirstList));
            const secondDisplayList = randomize(getDisplayList(secondList, availableSecondList));

            const moreDealsLink = getMoreDealsLink([...availableFirstList, ...availableSecondList].map((el) => el.id));

            return {
              moreDealsLink,
              deals: [
                checkValue(firstDisplayList, true) && firstDisplayList.length > 0 ? firstDisplayList[0] : '',
                checkValue(secondDisplayList, true) && secondDisplayList.length > 0 ? secondDisplayList[0] : '',
              ].filter(_ => _ !== ''),
            };
        } else {
          const availableList = getAvailableResults(results);
          const displayList = randomize(getDisplayList(results, availableList));
          const moreDealsLink = getMoreDealsLink(availableList.map((el) => el.id));

          return {
            moreDealsLink,
            deals: checkValue(displayList, true) ? displayList.slice(0, 2) : [],
          };
        }
      } else return [];
    } else return [];
  }, [hotelSearchData]);

  return (
    <DealCardView
      {...{
        ...props,
        searchData: deals,
        handleClick,
        moreDealsLink,
        isLoading,
        checkInDateFormattedInputFormat,
        checkOutDateFormattedInputFormat,
      }}
    />
  );
};

export default DealCardByIds;
